import type { IStoriesFolder } from "@/shared/types";
import { queryClient, reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import { foldersKeys } from "../folders/folders.keys";
import {
  storiesFolderApi,
  type IStoriesFolderCreateParams,
  type IStoriesFolderUpdateParams,
} from "./stories-folder.api";
import { storiesFolderKeys } from "./stories-folder.keys";

/**
 * The folder list every folder surface reads — the sidebar tree, the list's
 * folder rows, the Location filter. It is flat and nested in the hook, so
 * patching one folder's parentId re-nests all of them.
 */
const FOLDER_LIST_KEY = [...foldersKeys.folders];

export const useCreateStoriesFolderMutation = () => {
  return useMutation({
    mutationFn: (data: IStoriesFolderCreateParams) =>
      storiesFolderApi.createStoriesFolder(data),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...storiesFolderKeys.storiesFolders]);
      reactQueryUtils.invalidateQueries([
        ...storiesFolderKeys.storiesFoldersCount,
      ]);
    },
  });
};

export const useUpdateStoriesFolderMutation = () => {
  return useMutation({
    mutationFn: (data: IStoriesFolderUpdateParams) =>
      storiesFolderApi.updateStoriesFolder(data),
    /**
     * Applied to the cache first, so a folder dragged into another folder moves
     * as it is dropped. This also fixes a refresh that never happened: the
     * invalidations below name the "stories-folders" keys, and no folder surface
     * reads those — they all read the folders list, which nothing here touched.
     */
    onMutate: async (data: IStoriesFolderUpdateParams) => {
      await queryClient.cancelQueries({ queryKey: FOLDER_LIST_KEY });
      const previousFolders =
        queryClient.getQueryData<IStoriesFolder[]>(FOLDER_LIST_KEY);
      if (!previousFolders) return { previousFolders: undefined };

      queryClient.setQueryData<IStoriesFolder[]>(
        FOLDER_LIST_KEY,
        previousFolders.map((folder) =>
          folder._id === data.id
            ? {
                ...folder,
                // The endpoint takes `title`; the model field is `name`.
                ...(data.title !== undefined ? { name: data.title } : {}),
                ...(data.parentId !== undefined
                  ? { parentId: data.parentId }
                  : {}),
              }
            : folder,
        ),
      );
      return { previousFolders };
    },
    onError: (_error, _data, context) => {
      if (context?.previousFolders) {
        queryClient.setQueryData(FOLDER_LIST_KEY, context.previousFolders);
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries(FOLDER_LIST_KEY);
      reactQueryUtils.invalidateQueries([...storiesFolderKeys.storiesFolders]);
      reactQueryUtils.invalidateQueries([
        ...storiesFolderKeys.storiesFoldersCount,
      ]);
    },
  });
};

export const useDeleteStoriesFolderMutation = () => {
  return useMutation({
    mutationFn: (id: string) => storiesFolderApi.deleteStoriesFolder(id),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...storiesFolderKeys.storiesFolders]);
      reactQueryUtils.invalidateQueries([
        ...storiesFolderKeys.storiesFoldersCount,
      ]);
    },
  });
};
